import { RotateCw } from "lucide-react";
import { Link } from "react-router";

import { ErrorState, LoadingState } from "../../components/StatusView";
import { ServersTable } from "../../features/servers/ServersTable";
import { useServers } from "../../features/servers/serverHooks";
import { ServiceStatusWidget } from "../../components/ui/ServiceStatusWidget";
import { usePublicNetworkTelemetry } from "../../features/network/networkTelemetryHooks";

const count = (value: number | null | undefined) =>
  value == null ? "—" : value.toLocaleString("en-US");

export function ConsoleOverviewPage() {
  const telemetry = usePublicNetworkTelemetry();
  const { state, reload } = useServers();
  const freshness = telemetry.data?.freshness ?? "unavailable";
  const observed = freshness === "unavailable" ? null : telemetry.data;

  return (
    <div className="console-page">
      <div className="dashboard-preview-head">
        <div className="console-page-head">
          <h1 className="console-page-title" id="console-overview">
            Overview
          </h1>
          <p className="console-page-sub">Shared Global Fabric for your servers.</p>
        </div>
        <div className="dashboard-preview-head-actions">
          <Link to="/console/logs" className="btn btn--ghost btn--sm">
            View Logs
          </Link>
          <button
            type="button"
            className="preview-icon-btn"
            aria-label="Refresh Resources"
            onClick={telemetry.reload}
          >
            <RotateCw size={16} strokeWidth={1.6} absoluteStrokeWidth aria-hidden="true" />
          </button>
        </div>
      </div>

      <section className="overview-section" aria-labelledby="console-overview">
        <div className="service-status-grid">
          <ServiceStatusWidget
            variant="resource"
            name="Global Fabric"
            identifier="global · Shared resource"
            status={{
              tone: freshness === "fresh" ? "ok" : "danger",
              label: freshness === "fresh" ? "Online" : "Offline",
            }}
            metrics={[
              {
                label: freshness === "stale" ? "Last connected" : "Connected",
                value: count(observed?.connectionsActive),
                tooltip:
                  "Node-global connected users across Global Fabric, not your account's reserved capacity. Stale telemetry is the last observed occupancy.",
              },
              { label: "Region", value: "us-central1" },
              { label: "Events · lifetime", value: count(observed?.lifetime?.eventsDelivered) },
              { label: "Publishes · lifetime", value: count(observed?.lifetime?.publishes) },
            ]}
            samples={telemetry.samples}
            chartLabel="Active connections · samples collected this visit"
            emptyChartLabel={
              freshness === "fresh"
                ? "Collecting live connection samples"
                : freshness === "stale"
                  ? "Last known values · collection delayed"
                  : "Waiting for node telemetry"
            }

            action={{ label: "+ New Server", to: "/console/servers?resource=global" }}
            actionPlacement="header"
          />
        </div>
      </section>

      <section className="overview-section" aria-label="Servers">
        {state.status === "loading" ? (
          <LoadingState label="Loading servers…" />
        ) : state.status === "error" ? (
          <ErrorState message={state.message} onRetry={reload} />
        ) : (
          <ServersTable servers={state.data.items} />
        )}
      </section>
    </div>
  );
}
